import {
  useArticleNotifications,
  useArticleReachStatistics,
  companiesNamesOptions,
  deleteArticle,
  useArticleReadStatistics,
  articleReachOptions,
  useFullArticle,
  fullArticleOptions,
} from "@/api";
import ArticleViewer from "@/components/communication/ArticleViewer";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import {
  Accordion,
  Breadcrumbs,
  Button,
  Icon,
  InputField,
  LoadingDots,
  LoadingSpinner,
  MobilePreview,
  ProgressIndicator,
} from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Communication.module.scss";
import {
  Dispatch,
  SetStateAction,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  firebaseTimestampToDate,
  getBuildingId,
  NUMERIC_DATE_TIME_LOCALE,
} from "@/lib/utils";
import { format } from "date-fns-tz";
import NewsTile from "@/components/communication/NewsTile";
import {
  useRouterStore,
  useSnackbarStore,
  useArticleStore,
} from "@/store/zustandStore";
import { useQueryClient } from "@tanstack/react-query";
import {
  Article,
  ArticleFormType,
  ArticleReadStatistics,
  ArticleStatus,
  Company,
  ExtendedArticle,
  NotificationType,
  RecipientGroup,
} from "@/types";
import { Tooltip } from "react-tooltip";
import ArticleNotificationTile from "@/components/communication/ArticleNotificationTile";
import LangFormatSelector from "@/components/communication/LangFormatSelector";
import { useArticleActions } from "@/hooks/useArticleActions";
import { differenceInMinutes } from "date-fns";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute(
  "/_portal/building/communication/$articleId/",
)({
  component: RouteComponent,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(fullArticleOptions(params.articleId));
    queryClient.ensureQueryData(articleReachOptions(params.articleId));
  },
});

function RouteComponent() {
  const { articleId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();
  const { hasCommunication } = useWelcomeCommunicationAccess();
  const { i18n, t } = useTranslation();
  const { addNotification } = useSnackbarStore();
  const { setStoredArticle } = useArticleStore();
  const { data: article } = useFullArticle(articleId);
  const { data: reach, isLoading: isLoadingReach } =
    useArticleReachStatistics(articleId);
  const { data: readStatistics, isLoading: isLoadingReadStatistics } =
    useArticleReadStatistics(articleId, !!reach);
  const { data: recipientCompanies, isLoading: isLoadingCompanyNames } =
    useBuildingQuery<Partial<Company>[]>(
      companiesNamesOptions(article?.recipients?.companiesIds ?? []),
    );
  const { isLoading: isLoadingNotifications, data: notifications } =
    useArticleNotifications(articleId);
  const [publishModal, setPublishModal] = useState(0); // 0 = closed, 1 = publish, 2 = republish
  const [isDeleting, setIsDeleting] = useState(false);
  const [language, setLanguage] = useState(0);
  const [preview, setPreview] = useState(1);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [filteredReadStatistics, setFilteredReadStatistics] = useState<
    ArticleReadStatistics["companies"]
  >([]);
  const [showNotificationTooltip, setShowNotificationTooltip] = useState(false);

  const isRecentlyPublished = useMemo(() => {
    if (!article.publishedAt) return false;
    const published = firebaseTimestampToDate(article.publishedAt);
    const now = new Date();
    return differenceInMinutes(now, published) < 10;
  }, [article.publishedAt]);

  useEffect(() => {
    let showTimeout: number | undefined;
    let hideTimeout: number | undefined;

    if (
      isRecentlyPublished &&
      !isLoadingNotifications &&
      !notifications?.length
    ) {
      showTimeout = window.setTimeout(() => {
        setShowNotificationTooltip(true);
        hideTimeout = window.setTimeout(() => {
          setShowNotificationTooltip(false);
        }, 15000);
      }, 1000);
    }

    return () => {
      if (showTimeout !== undefined) {
        clearTimeout(showTimeout);
      }
      if (hideTimeout !== undefined) {
        clearTimeout(hideTimeout);
      }
    };
  }, [isRecentlyPublished, notifications, isLoadingNotifications]);

  const {
    isPublishing,
    isArchiving,
    isCancellingPublish,
    handleArchiveArticle,
    handleCancelPublish,
  } = useArticleActions({
    article,
    publishModal,
    setPublishModal,
  });

  useEffect(() => {
    if (!companySearchTerm) {
      setFilteredReadStatistics(readStatistics ?? []);
      return;
    } else {
      const searchTermLower = companySearchTerm.toLowerCase();
      const filtered = (readStatistics ?? []).filter((company) =>
        company.name.toLowerCase().includes(searchTermLower),
      );
      setFilteredReadStatistics(filtered);
    }
  }, [readStatistics, companySearchTerm]);

  const publishedDate = useMemo(
    () =>
      article.publishedAt ? firebaseTimestampToDate(article.publishedAt) : "",
    [article],
  );
  const archivationDate = useMemo(
    () =>
      article.archivedAt ? firebaseTimestampToDate(article.archivedAt) : "",
    [article],
  );
  const plannedPublishDate = useMemo(
    () =>
      article.plannedPublishOn
        ? firebaseTimestampToDate(article.plannedPublishOn)
        : "",
    [article],
  );
  const plannedArchivationDate = useMemo(
    () =>
      article.plannedArchivationOn
        ? firebaseTimestampToDate(article.plannedArchivationOn)
        : "",
    [article],
  );
  const recipientCompaniesList = useMemo(
    () =>
      !article?.recipients?.companiesIds?.length
        ? t("ARTICLE.ALL_COMPANIES").toLowerCase()
        : recipientCompanies
          ? recipientCompanies.map((c) => c.buildingTenantName).join(", ")
          : undefined,
    [article, recipientCompanies],
  );
  const totalReachCount = useMemo(
    () => reach?.recipients?.total ?? 0,
    [article, reach],
  );

  const newNotification = async () => {
    navigate({
      to: `/building/communication/new-notification/$articleId`,
      params: { articleId },
    });
  };

  const breadcrumbs = [
    {
      label: t("COMMUNICATION.HEADER"),
      path: `/building/communication`,
    },
    {
      label: `${i18n.language === "en" ? article.title.en : article.title.no}`,
      path: `/building/communication/${articleId}`,
    },
  ];

  const handleDeleteDraft = async () => {
    setIsDeleting(true);
    try {
      await deleteArticle(articleId);
      addNotification(
        t("NOTIFICATIONS.DOCUMENT_DELETED", {
          entity: t("COMMUNICATION.STATUSES.draft"),
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "available-articles"],
      });
      navigate({ to: "/building/communication" });
    } catch (error) {
      console.error(error);
      addNotification(
        t("NOTIFICATIONS.DELETE_FAILED", {
          entity: t("COMMUNICATION.STATUSES.draft").toLowerCase(),
        }),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const Viewer = (
    <Suspense>
      <ArticleViewer
        preview
        title={language === 0 ? article.title.no : article.title.en}
        author={article.author}
        content={language === 0 ? article.content.no : article.content.en}
        attachments={article.attachments}
        operational={article.disregardConsent}
        bannerImage={article.bannerImg}
      />
    </Suspense>
  );

  if (!allowBuildingAdminOrReceptionistAccess || !hasCommunication) return null;

  return (
    <div>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/communication",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <h1 className="sr-only">
        {t("COMMUNICATION.HEADER")}:{" "}
        {article.title[i18n.language as "en" | "no"]}
      </h1>
      {article.status === ArticleStatus.ARCHIVED && (
        <p
          className="banner"
          style={{ marginBottom: "var(--wlcm-spacing-sm)" }}
        >
          <Icon name="info" /> {t("ARTICLE.ARCHIVED_INFO")}
        </p>
      )}
      <div className={styles.adminNewsPageGrid}>
        <div className={styles.buttonsRow}>
          <LangFormatSelector
            lang={language}
            format={preview}
            setLang={setLanguage}
            setFormat={setPreview}
          />
          <div>
            <Button
              icon="content_copy"
              onClick={() => {
                setStoredArticle(article as ArticleFormType);
                navigate({ to: "/building/communication/new-article" });
              }}
            >
              {t("ARTICLE.ACTIONS.DUPLICATE")}
            </Button>
            {article.status !== ArticleStatus.ARCHIVED && (
              <Button
                icon="edit"
                onClick={() =>
                  navigate({
                    to: "/building/communication/$articleId/edit",
                    params: { articleId },
                  })
                }
              >
                {t("ARTICLE.ACTIONS.EDIT")}
              </Button>
            )}
            {article.status === ArticleStatus.DRAFT ? (
              <PublishButton
                article={article}
                isPublishing={isPublishing}
                setPublishModal={setPublishModal}
              />
            ) : article.status === ArticleStatus.PLANNED ? (
              <>
                <Button
                  warning
                  isLoading={isCancellingPublish}
                  onClick={handleCancelPublish}
                >
                  {t("ARTICLE.ACTIONS.CANCEL_PUBLISH")}
                </Button>
                <PublishButton
                  article={article}
                  isPublishing={isPublishing}
                  setPublishModal={setPublishModal}
                />
              </>
            ) : article.status === ArticleStatus.PUBLISHED ? (
              <Button
                icon="archive"
                dark
                isLoading={isArchiving}
                onClick={handleArchiveArticle}
              >
                {t("ARTICLE.ACTIONS.ARCHIVE")}
              </Button>
            ) : article.status === ArticleStatus.ARCHIVED ? (
              <Button
                success
                isLoading={isPublishing}
                onClick={() => setPublishModal(2)}
              >
                {t("ARTICLE.ACTIONS.REPUBLISH")}
              </Button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className={styles.preview}>
          {preview === 0 ? <MobilePreview>{Viewer}</MobilePreview> : Viewer}
        </div>
        <div className={styles.cardPreviewContainer}>
          <NewsTile article={article} preview />
          <NewsTile skeleton />
          <NewsTile skeleton />
        </div>
        <aside>
          <div>
            <h3 className="h4">Status</h3>
            <p className={styles.status}>
              <span
                className={`${styles.statusDot} ${styles[article.status]}`}
                aria-hidden
              ></span>
              {t(`COMMUNICATION.STATUSES.${article.status}`)}
            </p>
          </div>
          {publishedDate ? (
            <div>
              <h3 className="h4">{t("COMMUNICATION.PUBLISHED_AT")}</h3>
              <time dateTime={format(publishedDate, "yyyy-MM-dd' 'HH:mm")}>
                {NUMERIC_DATE_TIME_LOCALE(i18n.language).format(publishedDate)}
              </time>
            </div>
          ) : (
            plannedPublishDate && (
              <div>
                <h3 className="h4">{t("ARTICLE.PLANNED_PUBLISH.LABEL")}</h3>
                <span
                  style={{
                    fontWeight: "bold",
                    color: "var(--wlcm-color-success-green)",
                  }}
                >
                  {t("COMMUNICATION.STATUSES.planned")}{" "}
                </span>
                <time
                  dateTime={format(plannedPublishDate, "yyyy-MM-dd' 'HH:mm")}
                >
                  {NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
                    plannedPublishDate,
                  )}
                </time>
              </div>
            )
          )}
          {archivationDate ? (
            <div>
              <h3 className="h4">{t("COMMUNICATION.ARCHIVED_AT")}</h3>
              <time dateTime={format(archivationDate, "yyyy-MM-dd' 'HH:mm")}>
                {NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
                  archivationDate,
                )}
              </time>
            </div>
          ) : (
            plannedArchivationDate && (
              <div>
                <h3 className="h4">{t("ARTICLE.PLANNED_ARCHIVATION.LABEL")}</h3>
                <span style={{ fontWeight: "bold", opacity: 0.5 }}>
                  {t("COMMUNICATION.STATUSES.planned")}{" "}
                </span>
                <time
                  dateTime={format(
                    plannedArchivationDate,
                    "yyyy-MM-dd' 'HH:mm",
                  )}
                >
                  {NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
                    plannedArchivationDate,
                  )}
                </time>
              </div>
            )
          )}
          <div>
            <h3 className="h4">{t("ARTICLE.RECIPIENTS")}</h3>
            <p>
              {article.recipients.groups?.length === 0 ||
              article.recipients.groups?.includes(RecipientGroup.ALL)
                ? t("ARTICLE.NOTIFICATION_GROUPS.ALL_USERS")
                : t("ARTICLE.NOTIFICATION_GROUPS.ONLY_ADMINS")}{" "}
              {i18n.language === "en" ? "in" : "i"}{" "}
              {isLoadingCompanyNames && !recipientCompaniesList ? (
                <LoadingDots
                  style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    fontSize: "0.5em",
                  }}
                />
              ) : (
                recipientCompaniesList
              )}
            </p>
          </div>
          <div className={styles.asideColFlex}>
            <h3 className="h4">
              <Icon name="notifications_active" /> {t("ARTICLE.NOTIFICATIONS")}
            </h3>
            {isLoadingNotifications || !notifications ? (
              <>
                <LoadingSpinner />
              </>
            ) : notifications.length > 0 ? (
              <>
                {notifications
                  .sort(
                    (a, b) =>
                      (a.sentAt?._seconds ?? 0) - (b.sentAt?._seconds ?? 0),
                  )
                  .map((notification) => (
                    <ArticleNotificationTile
                      key={notification.id}
                      notification={notification}
                      articleTotalReach={
                        (notification.recipients.companyAdmins?.length ?? 0) +
                        (notification.recipients.users?.length ?? 0) +
                        (notification.recipients.cannotBeNotified?.length ?? 0)
                      }
                      couldNotBeNotified={
                        notification.recipients.cannotBeNotified?.length
                      }
                    />
                  ))}
              </>
            ) : (
              <p style={{ opacity: 0.5 }}>
                {t("ARTICLE.NO_NOTIFICATIONS_PLACEHOLDER")}
              </p>
            )}
            {article.status === ArticleStatus.PUBLISHED && (
              <>
                <Button
                  icon="mail"
                  secondary
                  blue
                  style={{
                    marginTop: "var(--wlcm-spacing-xs)",
                  }}
                  onClick={newNotification}
                  className="notification-button"
                  onMouseLeave={() => setShowNotificationTooltip(false)}
                >
                  {t("COMMUNICATION.NEW_NOTIFICATION")}
                </Button>
                <Tooltip
                  isOpen={showNotificationTooltip}
                  anchorSelect=".notification-button"
                  style={{
                    background: "var(--wlcm-color-blue)",
                    color: "black",
                  }}
                  place="bottom"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--wlcm-spacing-sm)",
                    }}
                  >
                    <Icon name="info" />
                    <p>
                      <strong>{t("ARTICLE.NO_NOTIFICATIONS_YET")}</strong>
                      <br />
                      {t("ARTICLE.CREATE_NOTIFICATION_HERE")}
                    </p>
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        </aside>
        <div className={styles.statistics}>
          {isLoadingReach ? (
            <div style={{ margin: "var(--wlcm-spacing-md) auto" }}>
              <LoadingSpinner />
            </div>
          ) : reach ? (
            <>
              {(article.status === ArticleStatus.PUBLISHED ||
                article.status === ArticleStatus.ARCHIVED) && (
                <div>
                  <figure>
                    <ProgressIndicator
                      id={`${articleId}--read-statistics`}
                      label={(data) => (
                        <Trans
                          i18nKey="COMMUNICATION.READ_STATS"
                          values={data}
                          components={{
                            strong: <strong />,
                            colored: (
                              <span
                                style={{
                                  color: "var(--wlcm-color-malling-purple)",
                                }}
                              />
                            ),
                            grey: <span style={{ opacity: 0.6 }} />,
                          }}
                        />
                      )}
                      total={totalReachCount}
                      value={article?.recipients?.readLog?.length ?? 0}
                      barColor="var(--wlcm-color-malling-purple)"
                    />
                  </figure>
                  {article?.recipients?.companiesIds?.length ===
                  1 ? null : isLoadingReadStatistics ? (
                    <LoadingSpinner />
                  ) : (readStatistics?.length ?? 0) > 5 ? (
                    <Accordion
                      id={`${articleId}--read-statistics-accordion`}
                      header={
                        <p
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--wlcm-spacing-xs)",
                          }}
                        >
                          <Icon name="filter_list" />
                          {t("COMMUNICATION.READ_STATS_PER_COMPANY")}
                        </p>
                      }
                      body={
                        <>
                          <InputField
                            compact
                            hideLabel
                            label={t("PAGES.COMPANIES.SEARCH")}
                            placeholder={t("PAGES.COMPANIES.SEARCH") + "..."}
                            icon="search"
                            value={companySearchTerm}
                            onChange={(e) =>
                              setCompanySearchTerm(e.target.value)
                            }
                            style={{ maxWidth: 400 }}
                          />
                          <CompaniesReadStatistics
                            article={article}
                            companies={filteredReadStatistics}
                            trackColor="#efefef"
                          />
                        </>
                      }
                      style={{
                        activator: {
                          padding: "var(--wlcm-spacing-sm)",
                          backgroundColor: "#b48ace1A",
                          borderRadius: "var(--wlcm-border-radius-sm)",
                        },
                        body: {
                          maxHeight: 300,
                          overflowY: "auto",
                          padding: "var(--wlcm-spacing-sm)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "var(--wlcm-spacing-sm)",
                          backgroundColor: "#b48ace0d",
                          borderRadius:
                            "0 0 var(--wlcm-border-radius-sm) var(--wlcm-border-radius-sm)",
                        },
                      }}
                    />
                  ) : (
                    <CompaniesReadStatistics
                      article={article}
                      companies={readStatistics}
                    />
                  )}
                </div>
              )}
              <figure>
                <ProgressIndicator
                  id={`${articleId}--recipients-statistics`}
                  label={(data) => (
                    <Trans
                      i18nKey="COMMUNICATION.RECIPIENTS_STATS"
                      values={{
                        tense: t(
                          article.status === ArticleStatus.PUBLISHED
                            ? "IS"
                            : article.status === ArticleStatus.ARCHIVED
                              ? "WAS"
                              : "WILL_BE",
                        ),
                        ...data,
                      }}
                      components={{
                        strong: <strong />,
                        colored: (
                          <span
                            style={{
                              color: "var(--wlcm-color-success-green)",
                            }}
                          />
                        ),
                        grey: <span style={{ opacity: 0.6 }} />,
                      }}
                    />
                  )}
                  total={reach.totalBuildingUsers}
                  values={[
                    {
                      id: "companyAdmins",
                      value: reach.recipients.adminsTotal,
                      color: "var(--wlcm-color-success-green)",
                      label: t("COMMUNICATION.COMPANY_ADMINS").toLowerCase(),
                    },
                    {
                      id: "users",
                      value: reach.recipients.regularTotal,
                      color: "#ABCCAB",
                      label: t("COMMUNICATION.REGULAR_USERS").toLowerCase(),
                    },
                  ]}
                  restCountLabel={t(
                    "COMMUNICATION.NON_RECIPIENTS",
                  ).toLowerCase()}
                />
              </figure>
            </>
          ) : (
            <></>
          )}
        </div>
        {article.status === ArticleStatus.DRAFT && (
          <div style={{ gridColumn: "1/-1" }}>
            <Button
              danger
              disabled={isDeleting || isPublishing}
              isLoading={isDeleting}
              style={{ marginLeft: "auto" }}
              onClick={handleDeleteDraft}
            >
              {t("ARTICLE.ACTIONS.DELETE_DRAFT")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const PublishButton = ({
  article,
  isPublishing,
  setPublishModal,
}: {
  article: ExtendedArticle;
  isPublishing: boolean;
  setPublishModal: Dispatch<SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  const isRecentlyCreated = useMemo(() => {
    if (!article.createdAt) return false;
    const created = firebaseTimestampToDate(article.createdAt);
    const now = new Date();
    return differenceInMinutes(now, created) < 2;
  }, [article.createdAt]);
  useEffect(() => {
    let showTimeout: number | undefined;
    let hideTimeout: number | undefined;

    if (isRecentlyCreated) {
      showTimeout = window.setTimeout(() => {
        setShowTooltip(true);
        hideTimeout = window.setTimeout(() => {
          setShowTooltip(false);
        }, 15000);
      }, 2000);
    }

    return () => {
      if (showTimeout !== undefined) {
        clearTimeout(showTimeout);
      }
      if (hideTimeout !== undefined) {
        clearTimeout(hideTimeout);
      }
    };
  }, [isRecentlyCreated]);

  return (
    <>
      <Button
        success
        isLoading={isPublishing}
        onClick={() => setPublishModal(1)}
        className="publish-button"
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* TODO: use different translation based on whether article has notification setup or not */}
        {t("ARTICLE.ACTIONS.PUBLISH_NOW")}
      </Button>
      <Tooltip
        isOpen={showTooltip}
        anchorSelect=".publish-button"
        style={{
          background: "var(--wlcm-color-warning-orange)",
          color: "black",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--wlcm-spacing-sm)",
          }}
        >
          <Icon name="info" />
          <p>
            <strong>{t("ARTICLE.STILL_A_DRAFT")}</strong>
            <br />
            {t("ARTICLE.REMEMBER_TO_PUBLISH")}
          </p>
        </div>
      </Tooltip>
    </>
  );
};

const CompaniesReadStatistics = ({
  article,
  companies,
  trackColor,
}: {
  article: Article;
  companies?: ArticleReadStatistics["companies"];
  trackColor?: string;
}) => {
  return (
    <>
      {(companies ?? []).map((company) => (
        <figure key={company.id}>
          <ProgressIndicator
            id={`${article.id}--read-statistics--${company.id}`}
            label={(data) => (
              <>
                <span
                  style={{
                    color: "var(--wlcm-color-malling-purple)",
                  }}
                >
                  {data.value}
                </span>{" "}
                / {data.total}{" "}
                <span style={{ opacity: 0.5 }}>
                  ({data.percentage}%) {company.name}
                </span>
              </>
            )}
            total={company.count}
            value={
              article.recipients.readLog?.filter(
                (read) => read.companyId === company.id,
              ).length ?? 0
            }
            trackWidth={Math.min(company.percentage, 80)} // max 80% width to make space for label
            barColor="#b48ace80"
            trackColor={trackColor}
            style={{ flexDirection: "row-reverse" }}
          />
        </figure>
      ))}
    </>
  );
};
